import * as React from "react";
import SearchForm from "../../containers/SearchForm";
import { Box, List, ListItem, Typography, Grid } from "@mui/material";
import { useEffect, useState } from "react";
import MultiSelectCheckbox from "../../components/MultiSelectCheckbox";
import { filterChoices } from "../../common/services/Filters";
import { DateFilterField } from "../DateFilter";
import { SearchRadioButton } from "../SearchRadioButton";
import ResultRunDate from "../ResultRunDate";
import { Query, Form, KeyValue } from "../../interfaces";
import AccordionDropdown from "../../containers/AccordionDropdown";
import {
  date,
  oscrFormSelectChoices,
  defaultSearchFieldInputLookup,
} from "../../refdata/constants";
import { FormSelectChoice } from "../../interfaces";
import SelectFilterField from "../SelectFilterField";
import { selectedStatesAlpha } from "../../refdata/FilterOptions";
import { getExpandState } from "../../common/services/Utilities";
import useMinLengthErrorValidation from "../../hooks/useMinLengthErrorValidation";
import TextFilterField from "../TextFilterField";
import useAppStateToFormChanges from "../../hooks/useAppStateToFormChanges";
import MultiDropdownInput from "../../containers/MultiDropdownInput";
import MoreInfoButton from "../MoreInfoButton";

const PREFIX = "StructureChangeForm";

const classes = {
  listDisplay: `${PREFIX}-listDisplay`,
};

interface Props {
  appState: Query;
  tableLevel: number;
}

export interface StructureChange extends Form {
  name: string;
  certSearch: string;
  eventCode: number[];
  startDate: string;
  endDate: string;
  searchDateRadio: string;
  stateSearch: string;
  city: string;
}

export default function StructureChangeForm({ appState, tableLevel }: Props) {
  const [structureChangeForm, setStructureChangeForm] =
    useState<StructureChange>({
      name: appState.search.name,
      stateSearch: appState.search.stateSearch,
      certSearch: appState.search.certSearch,
      eventCode: appState.filters.eventCode,
      startDate: appState.filters.startDate,
      endDate: appState.filters.endDate,
      searchDateRadio: appState.filters.searchDateRadio,
      city: appState.search.city,
    });

  const [disabled, setDisabled] = useState<boolean>(false);

  const [eventTypeFilterChoices, setEventTypeFilterChoices] = useState(() => {
    return filterChoices.eventCode.map((eventCodeChoice: KeyValue) => {
      return {
        ...eventCodeChoice,
        checked: eventCodeChoice.value.some((arr: number[]) =>
          appState.filters.eventCode.includes(arr),
        ),
      };
    });
  });
  const [errors, setErrors] = useState<Set<string>>(new Set());

  const structureChangeErrorMetaData = {
    name: {
      minLength: 4,
      helperText: "Minimum Length of 4 Characters",
    },
    city: {
      minLength: 3,
      helperText: "Minimum Length of 3 Characters",
    },
  };

  const dateErrors: KeyValue = React.useMemo(() => {
    return {};
  }, []);

  const toolTipForDates = (
    text: string,
    popperPosition: any,
    header: string,
    body: string,
  ) => {
    return (
      <Grid container alignItems="center" wrap="nowrap">
        <Grid sx={{ width: 140 }}>
          <Typography>{text}</Typography>
        </Grid>
        <Grid>
          <MoreInfoButton
            id="tooltip"
            popperPosition={popperPosition}
            label="tip"
            hover
          >
            <Box>
              <Typography color="primary.main" variant="h3" align="center">
                {header}
              </Typography>
              <Typography color="primary.main" variant="body2" align="center">
                {body}
              </Typography>
            </Box>
          </MoreInfoButton>
        </Grid>
      </Grid>
    );
  };

  const dateRadioButtons = [
    {
      value: "EFFDATE",
      id: "radio-effdate",
      label: toolTipForDates(
        "Search Effective Dates",
        "top",
        "Definition of Effective Dates",
        "The date indicating when the data for the record becomes valid.",
      ),
    },
    {
      value: "PROCDATE",
      id: "radio-procdate",
      label: toolTipForDates(
        "Search Processed Dates",
        "bottom",
        "Definition of Processed Dates",
        "The date the original record was processed.",
      ),
    },
  ];

  useEffect(() => {
    setDisabled(errors.size !== 0 || Object.keys(dateErrors).length > 0);
  }, [errors, dateErrors]);

  const { prevForm, setPrevForm } = useAppStateToFormChanges<StructureChange>(
    appState.filters,
    appState.search,
    setStructureChangeForm,
  );

  useEffect(() => {
    setEventTypeFilterChoices((prevState: any) => {
      return prevState.map((eventCodeChoice: KeyValue) => {
        return {
          ...eventCodeChoice,
          checked: eventCodeChoice.value.some((arr: number) =>
            appState.filters.eventCode.includes(arr),
          ),
        };
      });
    });
  }, [appState.filters.eventCode]);

  useMinLengthErrorValidation(
    structureChangeForm,
    structureChangeErrorMetaData,
    setErrors,
  );

  const endMinDate =
    new Date(structureChangeForm.endDate).getTime() >
    new Date(date.defaultMin).getTime()
      ? structureChangeForm.startDate
      : date.defaultMin;

  const structureChangeFormList = (
    <List className={classes.listDisplay}>
      <ListItem id="result-run-date-li">
        <ResultRunDate />
      </ListItem>
      <ListItem id="name-cert-li">
        <AccordionDropdown
          id="nameCert"
          title="Bank Name/Cert"
          defaultExpand={getExpandState(appState.ui.nameCertExpand, false)}
          body={
            <MultiDropdownInput
              selectId="name-cert"
              form={structureChangeForm}
              setForm={setStructureChangeForm}
              initialSearchFieldName={
                appState.search.name
                  ? "name"
                  : appState.search.certSearch
                    ? "certSearch"
                    : ""
              }
              formSelectChoices={oscrFormSelectChoices}
              searchFieldInputLookup={defaultSearchFieldInputLookup}
              errors={errors}
              formMetaData={structureChangeErrorMetaData}
            />
          }
        />
      </ListItem>
      <ListItem id="date-range-li">
        <AccordionDropdown
          id="dateRange"
          title="Date Range"
          defaultExpand={getExpandState(appState.ui.dateRangeExpand, true)}
          body={
            <>
              <DateFilterField
                currentValue={structureChangeForm.startDate}
                fieldName="startDate"
                label="Start Date"
                minDateValue={date.defaultMin}
                maxDateValue={structureChangeForm.endDate}
                errors={dateErrors}
                form={structureChangeForm}
                setForm={setStructureChangeForm}
              />
              <DateFilterField
                currentValue={structureChangeForm.endDate}
                fieldName="endDate"
                label="End Date"
                minDateValue={endMinDate}
                maxDateValue={new Date().toLocaleDateString()}
                errors={dateErrors}
                form={structureChangeForm}
                setForm={setStructureChangeForm}
              />
              <SearchRadioButton
                currentValue={structureChangeForm.searchDateRadio}
                ariaLabel="Search Dates"
                fieldName="searchDateRadio"
                name="Search Dates"
                radioButtons={dateRadioButtons}
                form={structureChangeForm}
                setForm={setStructureChangeForm}
              />
            </>
          }
        />
      </ListItem>
      {tableLevel === 2 ? (
        " "
      ) : (
        <ListItem id="event-type-li">
          <AccordionDropdown
            id="eventType"
            title="Event Type"
            defaultExpand={getExpandState(appState.ui.eventTypeExpand, true)}
            body={
              <MultiSelectCheckbox
                title="Event Type"
                choices={eventTypeFilterChoices as FormSelectChoice[]}
                setForm={setStructureChangeForm}
                fieldName={"eventCode"}
              />
            }
          />
        </ListItem>
      )}
      <ListItem id="locations-li">
        <AccordionDropdown
          id="locations"
          title="Locations"
          defaultExpand={getExpandState(appState.ui.locationsExpand, false)}
          body={
            <>
              <TextFilterField
                currentValue={structureChangeForm.city}
                apiPath="institutions"
                fieldName="city"
                label="City"
                autoSuggest
                form={structureChangeForm}
                setForm={setStructureChangeForm}
                error={errors.has("city")}
                regEx={"^[a-zA-Z\\s]*$"}
                helperText={
                  errors.has("city")
                    ? structureChangeErrorMetaData.city.helperText
                    : ""
                }
              />
              <SelectFilterField
                currentValue={structureChangeForm.stateSearch}
                fieldName="stateSearch"
                label="State/Territory"
                options={selectedStatesAlpha}
                form={structureChangeForm}
                setForm={setStructureChangeForm}
              />
            </>
          }
        />
      </ListItem>
    </List>
  );
  return (
    <SearchForm
      header="Search For Events"
      prevForm={prevForm}
      setPrevForm={setPrevForm}
      canHideSearchPanel={tableLevel === 2}
      formList={structureChangeFormList}
      form={structureChangeForm}
      disabled={disabled}
    />
  );
}
